import React, { useState } from 'react';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { Tabs, type TabItem } from '../../components/ui/Tabs/Tabs';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { ConfirmDialog } from '../../components/access/ConfirmDialog/ConfirmDialog';
import { OrganizationFormModal } from '../../components/access/OrganizationFormModal/OrganizationFormModal';
import type { OrganizationFormValues } from '../../components/access/OrganizationFormModal/OrganizationFormModal';
import { OrganizationsFilters } from '../../components/access/OrganizationsFilters/OrganizationsFilters';
import { OrganizationsTable } from '../../components/access/OrganizationsTable/OrganizationsTable';
import { UserFormModal } from '../../components/access/UserFormModal/UserFormModal';
import type {
    UserFormSubmitOptions,
    UserFormValues,
} from '../../components/access/UserFormModal/UserFormModal';
import { UsersFilters } from '../../components/access/UsersFilters/UsersFilters';
import { UsersTable } from '../../components/access/UsersTable/UsersTable';
import {
    useCreateAdminOrganizationMutation,
    useCreateAdminUserMutation,
    useDeleteAdminOrganizationMutation,
    useDeleteAdminUserMutation,
    useUpdateAdminOrganizationMutation,
    useUpdateAdminUserMutation,
    useSendUserLoginMutation,
    buildUpdateOrganizationBody,
    toInt,
    useReplaceAdminOrganizationMutation,
    useUpdateOrganizationScanLimitMutation,
    type UpdateAdminUserArgs,
} from '../../entities/access/api/accessApi';
import { useLoginAsMutation } from '../../entities/session/api/sessionApi';
import { ROLE_TO_DTO } from '../../entities/access/model/mappers';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectUser } from '../../entities/session/model/slice';
import {
    useOrganizationOptions,
    useOrganizationsFilters,
    useUsersFilters,
} from '../../hooks/useAccessFilters';
import type { AccessOrganization, AccessUser } from '../../types/access';
import { notifyError, notifySuccess, notifyWarning } from '../../shared/lib/notifications';
import { formatRangeLabel } from '../../utils/format';
import { formatNumber } from '../../utils/number';
import styles from './AccessPage.module.scss';
import { selectOrganization } from '../../entities/organization/model/slice';

type AccessTab = 'users' | 'organizations';

type Dialog =
    | { kind: 'user-form'; user: AccessUser | null }
    | { kind: 'user-delete'; user: AccessUser }
    | { kind: 'organization-form'; organization: AccessOrganization | null }
    | { kind: 'organization-delete'; organization: AccessOrganization }
    | null;

const ADMIN_ROLES = ['admin', 'super_admin', 'superadmin'];

/**
 * Текст ошибки из 400 PATCH /api/admin/users:
 *  - валидация/send_email без пароля: { error: 'BadRequest', message: '<текст>' }
 *  - бизнес-ошибки (email занят и т.п.): { message: 'Ошибка запроса', error: '<текст>' }
 */
function getUserApiError(cause: unknown): string | undefined {
    const data = (cause as { data?: { message?: unknown; error?: unknown } })?.data;
    if (!data) return undefined;

    if (data.message === 'Ошибка запроса' && typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
    return undefined;
}

export function AccessPage() {
    const [tab, setTab] = useState<AccessTab>('users');
    const [dialog, setDialog] = useState<Dialog>(null);
    const dispatch = useAppDispatch();

    const currentUser = useAppSelector(selectUser);
    const isAdmin = ADMIN_ROLES.includes(currentUser?.role ?? '');

    const users = useUsersFilters();
    const organizations = useOrganizationsFilters();
    const organizationOptions = useOrganizationOptions();

    const [createUser, createUserState] = useCreateAdminUserMutation();
    const [sendUserLogin] = useSendUserLoginMutation();
    const [updateUser, updateUserState] = useUpdateAdminUserMutation();
    const [deleteUser, deleteUserState] = useDeleteAdminUserMutation();
    const [loginAs] = useLoginAsMutation();
    const [created, setCreated] = useState<{ email: string; password: string } | null>(null);
    // Ошибка сохранения пользователя — показывается внутри окна, окно не закрывается.
    const [userError, setUserError] = useState<string | null>(null);

    const [createOrganization, createOrganizationState] = useCreateAdminOrganizationMutation();
    const [updateOrganization, updateOrganizationState] = useUpdateAdminOrganizationMutation();
    const [deleteOrganization, deleteOrganizationState] = useDeleteAdminOrganizationMutation();
    const [replaceOrganization, replaceOrganizationState] = useReplaceAdminOrganizationMutation();
    const [updateScanLimit, scanLimitState] = useUpdateOrganizationScanLimitMutation();

    const isUsers = tab === 'users';
    const close = (): void => {
        setDialog(null);
        setUserError(null);
    };

    const tabs: TabItem<AccessTab>[] = [
        { key: 'users', label: 'Пользователи', badge: { value: formatNumber(users.total) } },
        { key: 'organizations', label: 'Организации', badge: { value: formatNumber(organizations.total) } },
    ];

    async function submitUser(values: UserFormValues, { sendEmail }: UserFormSubmitOptions): Promise<void> {
        const editing = dialog?.kind === 'user-form' ? dialog.user : null;

        setUserError(null);

        try {
            if (editing) {
                // Только изменённые поля: неизменённый email бэкенд отклонит как «уже зарегистрирован».
                const patch: UpdateAdminUserArgs = { id: editing.id };

                if (values.fullName !== editing.fullName) patch.full_name = values.fullName;
                if (values.email.toLowerCase() !== editing.email.toLowerCase()) {
                    patch.email = values.email;
                }
                if (values.role !== editing.role) patch.role = ROLE_TO_DTO[values.role];
                if (values.password) patch.password = values.password;

                if (Object.keys(patch).length === 1) {
                    notifySuccess('Изменений нет');
                    close();
                    return;
                }

                // Кнопка «Сохранить и отправить на почту» активна только с новым паролем.
                if (sendEmail) patch.send_email = true;

                // Пароль в теле — в логи/консоль не выводить.
                const result = await updateUser(patch).unwrap();

                close();

                if (!sendEmail) {
                    notifySuccess('Пользователь изменён');
                } else if (result.email_sent) {
                    // email берём из ответа: если его меняли, письмо ушло на новый адрес
                    notifySuccess(`Пароль сохранён и отправлен на ${result.email}`);
                } else {
                    // Пароль уже действует — повторно жать кнопку не нужно
                    notifyWarning(
                        `Пароль сохранён, но письмо на ${result.email} не доставлено. ` +
                        'Передайте пароль пользователю другим способом',
                    );
                }
            } else {
                await createUser({
                    full_name: values.fullName,
                    email: values.email,
                    password: values.password,
                    role: ROLE_TO_DTO[values.role],
                    organization_ids: [values.organizationId],
                }).unwrap();

                notifySuccess('Пользователь создан');
                close();

                setCreated({ email: values.email, password: values.password });
            }
        } catch (cause) {
            const detail = getUserApiError(cause);

            if (editing) {
                // 400 — окно не закрываем, показываем ошибку в нём
                setUserError(detail ?? 'Не удалось изменить пользователя');
            } else {
                notifyError(detail ?? 'Не удалось создать пользователя');
            }
        }
    }

    async function confirmDeleteUser(user: AccessUser): Promise<void> {
        try {
            await deleteUser(user.id).unwrap();
            notifySuccess('Пользователь удалён');
            close();
        } catch {
            notifyError('Не удалось удалить пользователя');
        }
    }

    async function submitOrganization(values: OrganizationFormValues): Promise<void> {
        const editing = dialog?.kind === 'organization-form' ? dialog.organization : null;

        try {
            if (editing) {
                const body = buildUpdateOrganizationBody(editing, {
                    name: values.name,
                    inn: values.inn,
                    scan_limit_max: values.requestsLimit,
                });

                if (!body) {
                    await replaceOrganization({
                        organization_id: editing.id,
                        body: { name: values.name, inn: toInt(values.inn) },
                    }).unwrap();

                    if (values.requestsLimit !== editing.requestsLimit) {
                        await updateScanLimit({
                            organization_id: editing.id,
                            scan_limit_max: values.requestsLimit,
                        }).unwrap();
                    }
                } else {
                    await updateOrganization(body).unwrap();
                }

                notifySuccess('Организация изменена');
            } else {
                await createOrganization({
                    name: values.name,
                    inn: toInt(values.inn),
                    max_limit: values.requestsLimit,
                }).unwrap();

                notifySuccess('Организация создана');
            }

            close();
        } catch (cause) {
            const detail = (cause as { data?: { message?: string } })?.data?.message;
            notifyError(detail ?? 'Не удалось сохранить организацию');
        }
    }

    async function confirmDeleteOrganization(organization: AccessOrganization): Promise<void> {
        try {
            await deleteOrganization(organization.id).unwrap();
            notifySuccess('Организация удалена');
            close();
        } catch {
            notifyError('Не удалось удалить организацию');
        }
    }

    async function handleLoginAs(user: AccessUser): Promise<void> {
        if (!isAdmin) {
            notifyError('У вас недостаточно прав.');
            return;
        }

        try {
            if (sessionStorage.getItem('c_user') === null && currentUser) {
                sessionStorage.setItem('c_user', currentUser.id);
            }

            // await loginAs(user.id).unwrap();
            await loginAs({ userId: user.id, organizationId: user.organizationId ?? undefined }).unwrap();
            if (user.organizationId) {
                dispatch(selectOrganization(user.organizationId));
            }
        } catch {
            notifyError('Произошла ошибка при попытке входа в аккаунт');
        }
    }

    const isSavingUser = createUserState.isLoading || updateUserState.isLoading;
    const isSavingOrganization =
        createOrganizationState.isLoading ||
        updateOrganizationState.isLoading ||
        replaceOrganizationState.isLoading ||
        scanLimitState.isLoading;

    const usersEmpty = users.visible.length === 0 ? (
        <EmptyState
            title="Никого не нашли"
            description="Проверьте написание или сбросьте фильтры."
            action={users.isFiltered ? { label: 'Сбросить фильтры', onClick: users.reset } : undefined}
        />
    ) : undefined;

    const organizationsEmpty = organizations.visible.length === 0 ? (
        <EmptyState
            title="Организаций не нашлось"
            description="Проверьте написание или сбросьте фильтры."
            action={
                organizations.isFiltered
                    ? { label: 'Сбросить фильтры', onClick: organizations.reset }
                    : undefined
            }
        />
    ) : undefined;

    return (
        <div className={styles.page}>
            <PageHeader
                title="Доступы"
                className={styles.header}
                meta={[
                    <>
                        Пользователей <b>{formatNumber(users.total)}</b> в{' '}
                        <b>{formatNumber(organizations.total)}</b> организациях
                    </>,
                ]}
                actions={
                    <Button
                        variant="primary"
                        onClick={() =>
                            setDialog(
                                isUsers
                                    ? { kind: 'user-form', user: null }
                                    : { kind: 'organization-form', organization: null },
                            )
                        }
                    >
                        {isUsers ? 'Новый пользователь' : 'Новая организация'}
                    </Button>
                }
            />

            <Tabs items={tabs} active={tab} onChange={setTab} />

            {isUsers ? (
                <section className={styles.pane} role="tabpanel" aria-label="Пользователи">
                    <UsersFilters
                        organizations={organizationOptions}
                        query={users.query}
                        role={users.role}
                        organization={users.organization}
                        status={users.status}
                        isFiltered={users.isFiltered}
                        onQueryChange={users.setQuery}
                        onRoleChange={users.setRole}
                        onOrganizationChange={users.setOrganization}
                        onStatusChange={users.setStatus}
                        onReset={users.reset}
                    />

                    <UsersTable
                        users={users.visible}
                        isLoading={users.isLoading}
                        loadingText="Загружаем пользователей…"
                        scrollKey={`${users.page}-${users.query}-${users.role}-${users.organization}-${users.status}`}
                        footer={
                            users.total > 0 ? (
                                <TableFooter
                                    page={users.page}
                                    totalPages={users.pageCount}
                                    rangeLabel={formatRangeLabel(users.page, users.perPage, users.total)}
                                    onPageChange={users.setPage}
                                />
                            ) : undefined
                        }
                        empty={usersEmpty}
                        onOpen={handleLoginAs}
                        onEdit={(user) => setDialog({ kind: 'user-form', user })}
                        onDelete={(user) => setDialog({ kind: 'user-delete', user })}
                    />
                </section>
            ) : (
                <section className={styles.pane} role="tabpanel" aria-label="Организации">
                    <OrganizationsFilters
                        query={organizations.query}
                        status={organizations.status}
                        isFiltered={organizations.isFiltered}
                        onQueryChange={organizations.setQuery}
                        onStatusChange={organizations.setStatus}
                        onReset={organizations.reset}
                    />

                    <OrganizationsTable
                        organizations={organizations.visible}
                        isLoading={organizations.isLoading}
                        loadingText="Загружаем организации…"
                        scrollKey={`${organizations.page}-${organizations.query}-${organizations.status}`}
                        footer={
                            organizations.total > 0 ? (
                                <TableFooter
                                    page={organizations.page}
                                    totalPages={organizations.pageCount}
                                    rangeLabel={formatRangeLabel(
                                        organizations.page,
                                        organizations.perPage,
                                        organizations.total,
                                    )}
                                    onPageChange={organizations.setPage}
                                />
                            ) : undefined
                        }
                        empty={organizationsEmpty}
                        onEdit={(organization) => setDialog({ kind: 'organization-form', organization })}
                        onDelete={(organization) => setDialog({ kind: 'organization-delete', organization })}
                    />
                </section>
            )}

            {dialog?.kind === 'user-form' && (
                <UserFormModal
                    user={dialog.user}
                    isSaving={isSavingUser}
                    serverError={userError}
                    onSubmit={(values, options) => void submitUser(values, options)}
                    onClose={close}
                />
            )}

            {/* Только для новых пользователей: приветственное письмо с обучением.
                При редактировании письмо отправляет кнопка «Сохранить и отправить на почту». */}
            <ConfirmModal
                open={created !== null}
                title="Отправить данные аккаунта?"
                description={`Логин и пароль уйдут на ${created?.email}.`}
                confirmLabel="Отправить"
                cancelLabel="Отмена"
                onConfirm={async () => {
                    if (!created) return;
                    try {
                        await sendUserLogin(created).unwrap();
                        notifySuccess('Данные отправлены пользователю');
                    } catch {
                        notifyError('Не удалось отправить данные');
                    }
                    setCreated(null);
                }}
                onClose={() => setCreated(null)}
            />

            {dialog?.kind === 'user-delete' && (
                <ConfirmDialog
                    title="Удалить пользователя?"
                    description={`${dialog.user.fullName} — восстановить удалённого пользователя нельзя.`}
                    isBusy={deleteUserState.isLoading}
                    onConfirm={() => void confirmDeleteUser(dialog.user)}
                    onClose={close}
                />
            )}

            {dialog?.kind === 'organization-form' && (
                <OrganizationFormModal
                    organization={dialog.organization}
                    isSaving={isSavingOrganization}
                    onSubmit={(values) => void submitOrganization(values)}
                    onClose={close}
                />
            )}

            {dialog?.kind === 'organization-delete' && (
                <ConfirmDialog
                    title="Удалить организацию?"
                    description={`${dialog.organization.name} — вместе с ней потеряется доступ у её пользователей.`}
                    isBusy={deleteOrganizationState.isLoading}
                    onConfirm={() => void confirmDeleteOrganization(dialog.organization)}
                    onClose={close}
                />
            )}
        </div>
    );
}